import cx from 'classnames'
import dynamic from 'next/dynamic'
import { useIntersectionObserver } from 'usehooks-ts'

import { SanityImageFragment } from '@data/sanity/queries/types/image'
import {
  SanityColor,
  SanityLargeMediaTextModule,
} from '@data/sanity/queries/types/modules'
import { SanityMuxVideo } from '@data/sanity/queries/types/video'

import Icon from '@components/icon'
import Photo from '@components/photo'
import { colorClasses } from '@components/portable-text/blocks/colored-text'
import ComplexPortableText from '@components/portable-text/complex'
const MuxPlayer = dynamic(() => import('@components/video/mux-player'))

type LargeMediaTextProps = Pick<
  SanityLargeMediaTextModule,
  'mediaType' | 'image' | 'video' | 'content' | 'frameColor'
>

interface LargeMediaTextVideoProps {
  video: SanityMuxVideo
}

interface LargeMediaTextPhotoProps {
  image: SanityImageFragment
}

interface LargeMediaTextContentFrameProps {
  frameColor: SanityColor
}

const LargeMediaTextVideo = ({ video }: LargeMediaTextVideoProps) => {
  const { ref: videoRef, isIntersecting: isVideoIntersecting } =
    useIntersectionObserver({
      freezeOnceVisible: true,
      threshold: 0.1,
    })

  return (
    <div
      ref={videoRef}
      className="relative w-full h-[480px] sm:h-[600px] lg:h-[900px] overflow-hidden z-0"
    >
      {isVideoIntersecting && (
        <MuxPlayer
          assetDocument={video.asset}
          showControls={false}
          autoplay={true}
          autoplayOnHover={false}
          openModalOnClick={false}
          muted={true}
          loop={true}
          playsInline={true}
          videoStyle={{
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100%',
            width: '100%',
          }}
        />
      )}
    </div>
  )
}

const LargeMediaTextPhoto = ({ image }: LargeMediaTextPhotoProps) => {
  return (
    <Photo
      image={image}
      fill
      className="relative w-full h-[480px] sm:h-[600px] lg:h-[900px] z-0"
      imageClassName="object-cover"
    />
  )
}

const LargeMediaTextContentFrame = ({
  frameColor,
}: LargeMediaTextContentFrameProps) => {
  return (
    <>
      <Icon
        name="FrameCorner"
        className={cx(
          'absolute -top-4 -left-4 text-[32px] pointer-events-none',
          colorClasses[frameColor],
        )}
      />
      <Icon
        name="FrameCorner"
        className={cx(
          'absolute -top-4 -right-4 rotate-90 text-[32px] pointer-events-none',
          colorClasses[frameColor],
        )}
      />
    </>
  )
}

const LargeMediaText = ({
  mediaType,
  image,
  video,
  content,
  frameColor,
}: LargeMediaTextProps) => {
  return (
    <section className="relative overflow-hidden bg-pageBG">
      <div className="min-h-24">
        {mediaType === 'video' && !!video && (
          <LargeMediaTextVideo video={video} />
        )}

        {mediaType === 'image' && !!image && (
          <LargeMediaTextPhoto image={image} />
        )}
      </div>

      <div className="relative -mt-24 mx-10 md:mx-24">
        <div className="relative inline-block pt-10 pb-14 px-10 rounded-4xl bg-white z-10">
          {!!frameColor && (
            <LargeMediaTextContentFrame frameColor={frameColor} />
          )}

          <ComplexPortableText
            content={content}
            className="flex flex-col max-w-[800px] rc"
          />
        </div>
      </div>
    </section>
  )
}

export default LargeMediaText
